import React, { useEffect, useMemo, useState } from 'react';
import { Alert, DatePicker, Input, Modal, Radio, Select, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import lib from '../../assets/utils/lib';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import {
  canManageClaimsArchive,
  getClaimsSegmentLabel,
  isFacilityScopedUser,
  normalizeClaimsSegment,
  parseArchiveApiBody,
  withClaimsArchiveScope,
} from './claimsArchiveHelpers';

const { Text } = Typography;

/**
 * Both month-scoped facility endpoints clamp the page size the same way —
 * `max(1, min(100, pageSize))` in Claims::getAdminMonthFacilities and
 * Claims::getPaymentStatusMonthFacilities (api_v1/classes/Routes/Claims.php).
 * Asking for more than 100 silently gets you 100, so the picker asks for exactly
 * the cap and then walks the remaining pages using `pagination.total`.
 */
const FACILITY_PAGE_SIZE = 100;
/**
 * A safety stop, not a page budget: 50 pages is 5,000 facilities with claims in
 * a single month, far beyond the real directory. If it is ever reached the UI
 * says the list was cut short rather than quietly presenting a partial picker.
 */
const FACILITY_MAX_PAGES = 50;
/** The full-directory fallback's own ceiling, stated in the UI when it is used. */
const DIRECTORY_FALLBACK_LIMIT = 200;

function formatMonthLabel(month) {
  if (!month) return '';
  const d = dayjs(`${month}-01`);
  return d.isValid() ? d.format('MMMM YYYY') : month;
}

/**
 * `myApi` does not throw: a failed request comes back as `{ error: 1, data: [],
 * message }`, and a backend-level failure as `{ data: { error: 1, message } }`.
 * Both have to be recognised here, or an empty page reads as "no facilities".
 *
 * Response shapes differ between the two month-scoped endpoints:
 *   - `GET /months/{month}/facilities` → `data: { month, month_label, facilities: [] }`
 *   - `GET /payment/statuses/{segment}/months/{month}/facilities` → `data: []`
 * Both carry `pagination: { page, pageSize, total, totalPages }` at the root.
 */
function readFacilityPage(resp) {
  if (resp && Number(resp.error) > 0) {
    return { ok: false, message: resp.message || null, rows: [], total: 0, pageSize: 0 };
  }
  const body = resp?.data || {};
  if (Number(body.error) > 0) {
    return { ok: false, message: body.message || null, rows: [], total: 0, pageSize: 0 };
  }
  const payload = body.data;
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.facilities)
      ? payload.facilities
      : [];
  const total = Number(body.pagination?.total);
  const pageSize = Number(body.pagination?.pageSize);
  return {
    ok: true,
    message: null,
    rows,
    total: Number.isFinite(total) && total >= 0 ? total : rows.length,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 0,
  };
}

/**
 * One option per facility. The claim count rides in the label so the operator can
 * see the list really is month-scoped, and `name` keeps the bare facility name for
 * the confirmation copy — which must not read "Foo — 12 claims in December".
 */
function toFacilityOption(row) {
  const value = Number(row?.facility_id ?? row?.id);
  const name = row?.facility_name
    || row?.name
    || (Number.isFinite(value) ? `Facility ${value}` : 'Facility');
  const count = Number(row?.claims_count);
  const hasCount = Number.isFinite(count) && count >= 0;
  return {
    value,
    name,
    label: hasCount
      ? `${name} — ${count.toLocaleString()} claim${count === 1 ? '' : 's'}`
      : name,
  };
}

function dedupeFacilityOptions(rows) {
  const seen = new Set();
  const opts = [];
  rows.forEach((row) => {
    const opt = toFacilityOption(row);
    if (!Number.isFinite(opt.value) || opt.value <= 0 || seen.has(opt.value)) return;
    seen.add(opt.value);
    opts.push(opt);
  });
  return opts;
}

export default function ArchiveClaimsModal({
  open,
  onClose,
  onSuccess,
  request,
  mode = 'archive',
  title,
  allowPick = false,
  lockedFacility = false,
  initialFacilityId,
  initialFacilityName,
  initialMonth,
  /**
   * Opt-in, per call site: offer "every facility in this month" alongside the
   * default facility+month scope. Only pass it where a month is genuinely the
   * unit of work and the caller is admin-side — a facility drilldown must not
   * hand out an action that reaches beyond the facility being looked at.
   */
  allowMonthWide = false,
  /** Facilities with claims in this month, for scale in the preview. Optional. */
  monthWideFacilityCount = null,
  /**
   * Payment-stage segment (`claims_status`) the operator is standing in: 0 =
   * Pending CO, 1 = Pending HM, 2 = Pending CFO, 3 = Paid. Archiving must act on
   * the queue it was invoked from, so a status-scoped list has to hand its own
   * segment down. `null`/omitted means the call site genuinely has no segment,
   * and the dialog then says so — "All statuses" — rather than implying otherwise.
   */
  segment = null,
}) {
  const { user } = useAuth();
  const { permissions } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState('');
  const [facilityId, setFacilityId] = useState(null);
  const [facilityName, setFacilityName] = useState('');
  const [month, setMonth] = useState(null);
  const [monthWide, setMonthWide] = useState(false);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  /** 'segment' | 'month' | 'directory' — which list the operator is looking at. */
  const [facilitySource, setFacilitySource] = useState(null);
  /** Why the month-scoped list was abandoned, shown verbatim in the fallback alert. */
  const [facilityFallbackReason, setFacilityFallbackReason] = useState(null);
  /** Set only when FACILITY_MAX_PAGES was hit, i.e. the list really is cut short. */
  const [facilitiesTruncated, setFacilitiesTruncated] = useState(null);
  /** A previously chosen facility that has no claims in the newly chosen month. */
  const [droppedFacilityName, setDroppedFacilityName] = useState(null);
  const isArchive = mode !== 'unarchive';

  const pickerMode = allowPick || !request;
  const requestScope = request?.scope || null;

  // An explicit request carries its own segment (buildClaimsArchiveRequest put it
  // there); the prop covers call sites that hand us no prebuilt request. Both go
  // through normalizeClaimsSegment so segment 0 survives — `||` would lose it.
  const requestSegment = normalizeClaimsSegment(request?.segment);
  const effectiveSegment = requestSegment !== null ? requestSegment : normalizeClaimsSegment(segment);
  const hasSegment = effectiveSegment !== null;
  const segmentLabel = hasSegment ? getClaimsSegmentLabel(effectiveSegment) : 'All statuses';

  // The backend pins facility users to their own facility and coerces a month
  // scope back to facility (ClaimsArchiveService::applyActorFacilityConstraint).
  // Mirror that here so the wider option is never even offered to them.
  const canArchiveClaims = canManageClaimsArchive(user, permissions);
  const monthWideAvailable = Boolean(
    allowMonthWide
    && canArchiveClaims
    && !lockedFacility
    && !isFacilityScopedUser(user)
    && requestScope !== 'claim',
  );
  // A caller that hands us an explicit month-scope request still gets the
  // wide-action gate, whether or not it also opted into the picker.
  const monthWideSelected = monthWide && (monthWideAvailable || requestScope === 'month');
  // Only offer the toggle where narrowing back to one facility is coherent: a
  // caller whose whole request is the month has no facility to fall back to.
  const showScopePicker = monthWideAvailable && (pickerMode || requestScope !== 'month');

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setReason('');
      setMonthWide(false);
      // The facility list belongs to one month; a re-open must not inherit the
      // last one's options or its warnings.
      setFacilityOptions([]);
      setFacilitySource(null);
      setFacilityFallbackReason(null);
      setFacilitiesTruncated(null);
      setDroppedFacilityName(null);
      return undefined;
    }

    const fromRequest = request?.facility_id ?? request?.facilityId ?? initialFacilityId ?? null;
    const fromMonth = request?.month ?? initialMonth ?? null;
    setFacilityId(fromRequest != null && fromRequest !== '' ? Number(fromRequest) : null);
    setFacilityName(initialFacilityName || '');
    setMonth(fromMonth || null);
    setMonthWide(request?.scope === 'month');
    setReason('');
    setPreview(null);
    return undefined;
  }, [open, request, initialFacilityId, initialFacilityName, initialMonth]);

  // The picker offers only facilities that actually have claims in the chosen
  // month — and, when the operator is standing in a payment queue, only those
  // with claims at that stage. Offering the whole directory here meant most
  // choices archived nothing, and a hard `limit: 200` could hide a facility that
  // did have claims. Both lists are month-first, so this depends on the month
  // (and segment) and re-runs whenever either changes.
  useEffect(() => {
    // A locked facility has nothing to pick, so it never fetches.
    if (!open || !pickerMode || lockedFacility) {
      return undefined;
    }
    if (!month) {
      // No month means no honest list to show. Say nothing rather than something
      // wrong; the select stays disabled and asks for the month first.
      setFacilityOptions([]);
      setFacilitySource(null);
      setFacilityFallbackReason(null);
      setFacilitiesTruncated(null);
      setDroppedFacilityName(null);
      setFacilitiesLoading(false);
      return undefined;
    }

    let cancelled = false;
    setFacilitiesLoading(true);
    setFacilityFallbackReason(null);
    setFacilitiesTruncated(null);
    setDroppedFacilityName(null);

    // Restoring is the mirror of archiving: the facilities worth listing are the
    // ones holding archived claims, not active ones. Same `scope` query param the
    // drilldown lists already use.
    const listScope = isArchive ? 'active' : 'archived';
    const fetchPage = (page) => {
      const params = withClaimsArchiveScope(
        { page, pageSize: FACILITY_PAGE_SIZE },
        listScope,
      );
      return hasSegment
        ? lib.getPaymentStatusFacilities(effectiveSegment, month, params)
        : lib.getAdminMonthFacilities(month, params);
    };

    const applyOptions = (opts) => {
      setFacilityOptions(opts);
      const current = Number(facilityId);
      const match = Number.isFinite(current) ? opts.find((o) => o.value === current) : null;
      if (match) {
        setFacilityName(match.name);
      }
      return match;
    };

    (async () => {
      try {
        const first = readFacilityPage(await fetchPage(1));
        if (!first.ok) {
          throw new Error(first.message || 'The month list could not be loaded.');
        }
        const pageSize = first.pageSize || FACILITY_PAGE_SIZE;
        const totalPages = Math.max(1, Math.ceil(first.total / pageSize));
        const pagesToRead = Math.min(totalPages, FACILITY_MAX_PAGES);
        let rows = first.rows;
        if (pagesToRead > 1) {
          const rest = await Promise.all(
            Array.from({ length: pagesToRead - 1 }, (unused, i) => fetchPage(i + 2).then(readFacilityPage)),
          );
          // A page that fails leaves a hole nobody can see, and a hole here means
          // a facility with claims is missing from the picker. Treat it as a
          // failure of the whole list rather than shipping a silent gap.
          const failed = rest.find((p) => !p.ok);
          if (failed) {
            throw new Error(failed.message || 'Part of the month list could not be loaded.');
          }
          rows = rows.concat(...rest.map((p) => p.rows));
        }
        if (cancelled) return;
        const opts = dedupeFacilityOptions(rows);
        const match = applyOptions(opts);
        setFacilitySource(hasSegment ? 'segment' : 'month');
        setFacilitiesTruncated(totalPages > FACILITY_MAX_PAGES ? first.total : null);
        // Changing the month can strip the claims out from under an earlier
        // choice. Drop it rather than let it be archived to no effect.
        if (!match && facilityId) {
          setDroppedFacilityName(facilityName || `Facility ${facilityId}`);
          setFacilityId(null);
          setFacilityName('');
          setPreview(null);
        }
      } catch (e) {
        if (cancelled) return;
        // Last resort only. The directory is not month-scoped and is capped, so
        // the UI says exactly that instead of passing it off as the real list.
        try {
          const resp = await lib.getFacilitiesV2({ page: 1, limit: DIRECTORY_FALLBACK_LIMIT });
          if (cancelled) return;
          if (resp && Number(resp.error) > 0) {
            throw new Error(resp.message || 'Facility list unavailable');
          }
          const body = resp?.data;
          const list = Array.isArray(body?.data)
            ? body.data
            : Array.isArray(body?.data?.facilities)
              ? body.data.facilities
              : [];
          applyOptions(dedupeFacilityOptions(list));
        } catch (fallbackError) {
          if (cancelled) return;
          setFacilityOptions([]);
        }
        setFacilitySource('directory');
        setFacilityFallbackReason(e?.message || 'The month list could not be loaded.');
      } finally {
        if (!cancelled) setFacilitiesLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, pickerMode, lockedFacility, month, hasSegment, effectiveSegment, isArchive]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveMonth = month || request?.month || null;

  const resolvedRequest = useMemo(() => {
    // The segment rides along on every scope, widening included. Widening from
    // one facility to the whole month must not also widen from one queue to all
    // of them: the operator asked for "the rest of Pending CO", not "everything".
    const withSegment = (body) => (
      body && effectiveSegment !== null ? { ...body, segment: effectiveSegment } : body
    );
    // Month-wide: `{ scope: 'month', month }` — no facility_id, so the backend
    // sweeps every facility with claims that month.
    if (monthWideSelected) {
      return effectiveMonth ? withSegment({ scope: 'month', month: effectiveMonth }) : null;
    }
    if (!pickerMode && request) {
      return withSegment(request);
    }
    if (!facilityId || !month) {
      return null;
    }
    return withSegment({
      facility_id: facilityId,
      month,
      scope: 'facility',
    });
  }, [monthWideSelected, effectiveMonth, pickerMode, request, facilityId, month, effectiveSegment]);

  useEffect(() => {
    if (!open || !resolvedRequest) {
      setPreview(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const payload = {
          ...resolvedRequest,
          archived: isArchive ? undefined : 1,
        };
        const resp = await lib.previewClaimsArchive(payload);
        const parsed = parseArchiveApiBody(resp);
        if (!cancelled) {
          setPreview(parsed);
          if (parsed.facilityName) {
            setFacilityName(parsed.facilityName);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setPreview({ error: e?.message || 'Preview failed', eligible: 0, skipped: [] });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, resolvedRequest, isArchive]);

  const submitRequest = async () => {
    if (!resolvedRequest) return;
    setLoading(true);
    try {
      const payload = { ...resolvedRequest, reason: reason.trim() || undefined };
      const resp = isArchive
        ? await lib.archiveClaims(payload)
        : await lib.unarchiveClaims(payload);
      const parsed = parseArchiveApiBody(resp);
      if (!parsed.ok) {
        Modal.error({ title: isArchive ? 'Archive failed' : 'Unarchive failed', content: parsed.message || 'Request failed' });
        return;
      }
      onSuccess?.(parsed);
      onClose?.();
    } catch (e) {
      Modal.error({
        title: isArchive ? 'Archive failed' : 'Unarchive failed',
        content: e?.message || 'Request failed',
      });
    } finally {
      setLoading(false);
    }
  };

  // `GET|POST /v2/admin/claims/archive/preview` with scope=month answers
  // `{ scope: 'month', eligible_count }` and nothing else — no facility name, no
  // month label. It also ignores `archived`, so on a month-wide restore the
  // number it returns counts *active* archivable claims, not the archived ones
  // that would be restored. Rather than print a wrong number, we withhold it.
  const monthWideRestore = monthWideSelected && !isArchive;
  const previewCount = preview?.previewCount ?? preview?.archivedCount ?? preview?.eligible;
  const eligible = monthWideRestore ? null : previewCount;
  const skipped = preview?.skipped || [];
  // A preset multi-month request (the month list's "Archive selected") names its
  // months and reaches every facility; it never enters the picker.
  const requestMonths = requestScope === 'months' && Array.isArray(request?.months) ? request.months : null;
  const multiMonth = Boolean(requestMonths && requestMonths.length);
  const displayFacility = preview?.facilityName || facilityName || (facilityId ? `Facility ${facilityId}` : '');
  const displayMonth = multiMonth
    ? requestMonths.map(formatMonthLabel).join(', ')
    : (preview?.monthLabel || formatMonthLabel(resolvedRequest?.month || month));
  const canSubmit = Boolean(resolvedRequest)
    && !preview?.error
    && (eligible != null || (monthWideRestore && Boolean(preview)));

  // The scope is stated, never inferred: who, which month, and which queue.
  // e.g. "All facilities · December 2023 · Pending CO — 1 claim".
  const scopeWho = (monthWideSelected || multiMonth) ? 'All facilities' : (displayFacility || 'This facility');
  const scopeLine = `${scopeWho} · ${displayMonth || 'Month not chosen'} · ${segmentLabel}`;
  const scopeCountText = eligible != null
    ? `${eligible} claim${eligible === 1 ? '' : 's'}`
    : null;
  const scopeSummary = scopeCountText ? `${scopeLine} — ${scopeCountText}` : scopeLine;
  // A queue name only belongs in a sentence when there is one; without a segment
  // the sentence must not read as if it were narrowed.
  const segmentWord = hasSegment ? <><strong>{segmentLabel}</strong>{' '}</> : null;
  const showAllStatusesNote = !hasSegment && Boolean(resolvedRequest) && !preview?.error;

  // Copy for the facility picker. It states which list is on screen, because the
  // month-scoped list and the full directory answer different questions and an
  // operator who cannot tell them apart cannot trust either.
  const facilityMonthLabel = formatMonthLabel(month) || 'this month';
  const facilityClaimsPhrase = hasSegment
    ? `${segmentLabel} claims in ${facilityMonthLabel}`
    : `claims in ${facilityMonthLabel}`;
  const archivedWord = isArchive ? '' : 'archived ';
  const monthScoped = facilitySource === 'segment' || facilitySource === 'month';
  const facilityCount = facilityOptions.length;
  const facilityPlaceholder = facilitiesLoading ? 'Loading facilities…' : 'Select facility';
  const facilityEmptyText = monthScoped
    ? `No facilities have ${archivedWord}${facilityClaimsPhrase}.`
    : 'No facilities';
  const facilityHint = (() => {
    if (!month) {
      return 'Choose the month first — this list only offers facilities with claims in that month.';
    }
    if (facilitiesLoading || !monthScoped) return null;
    if (facilityCount === 0) return facilityEmptyText;
    return `${facilityCount.toLocaleString()} facilit${facilityCount === 1 ? 'y' : 'ies'} with `
      + `${archivedWord}${facilityClaimsPhrase}. Facilities with none are not listed.`;
  })();
  const showFacilityNotices = pickerMode && !lockedFacility && !monthWideSelected;

  const monthWideScaleCopy = (
    <>
      {eligible != null ? (
        <>
          <strong>{eligible}</strong> {segmentWord}claim{eligible === 1 ? '' : 's'} in{' '}
          <strong>{displayMonth || 'this month'}</strong>
        </>
      ) : (
        <>Every archived {segmentWord}claim in <strong>{displayMonth || 'this month'}</strong></>
      )}
      {', across '}
      {monthWideFacilityCount ? (
        <>all <strong>{monthWideFacilityCount}</strong> facilities</>
      ) : (
        <>every facility</>
      )}
      {' with claims that month.'}
    </>
  );

  const confirmCopy = (() => {
    if (multiMonth) {
      const n = requestMonths.length;
      return isArchive ? (
        <>
          Archive{' '}
          {eligible != null ? <><strong>{eligible}</strong> {segmentWord}claim{eligible === 1 ? '' : 's'}</> : <>{segmentWord}claims</>}{' '}
          in <strong>{n} month{n === 1 ? '' : 's'}</strong> ({displayMonth}) for <strong>every facility</strong>?
        </>
      ) : (
        <>
          Restore every archived {segmentWord}claim in <strong>{n} month{n === 1 ? '' : 's'}</strong> ({displayMonth}) for{' '}
          <strong>every facility</strong>?
        </>
      );
    }
    if (monthWideSelected && !effectiveMonth) {
      return 'Choose a month to see how many claims this will affect.';
    }
    if (monthWideSelected) {
      return isArchive ? (
        <>
          Archive{' '}
          {eligible != null ? (
            <>
              <strong>{eligible}</strong> {segmentWord}claim{eligible === 1 ? '' : 's'}
            </>
          ) : (
            <>{segmentWord}claims</>
          )}{' '}
          in <strong>{displayMonth || 'this month'}</strong> for{' '}
          <strong>every facility</strong>?
        </>
      ) : (
        <>
          Restore every archived {segmentWord}claim in <strong>{displayMonth || 'this month'}</strong> for{' '}
          <strong>every facility</strong>?
        </>
      );
    }
    if (eligible == null) {
      return isArchive
        ? 'Choose a facility and month to see how many claims will be archived.'
        : 'Choose a facility and month to see how many archived claims will be restored.';
    }
    if (isArchive) {
      return (
        <>
          Archive <strong>{eligible}</strong> {segmentWord}claim{eligible === 1 ? '' : 's'} for{' '}
          <strong>{displayFacility || 'this facility'}</strong> in <strong>{displayMonth || 'this month'}</strong>?
        </>
      );
    }
    return (
      <>
        Restore <strong>{eligible}</strong> archived {segmentWord}claim{eligible === 1 ? '' : 's'} for{' '}
        <strong>{displayFacility || 'this facility'}</strong> in <strong>{displayMonth || 'this month'}</strong>?
      </>
    );
  })();

  // A month-wide sweep never goes straight through the OK button: it asks again,
  // in different words, naming the scale it is about to act on.
  const handleConfirm = () => {
    if (!resolvedRequest) return;
    if (!monthWideSelected && !multiMonth) {
      submitRequest();
      return;
    }
    Modal.confirm({
      title: isArchive
        ? `Archive every facility for ${displayMonth || 'this month'}?`
        : `Restore every facility for ${displayMonth || 'this month'}?`,
      centered: true,
      width: 520,
      zIndex: 1100,
      okText: isArchive
        ? (eligible != null ? `Archive all ${eligible} claims` : 'Archive all facilities')
        : 'Restore all facilities',
      okButtonProps: { danger: isArchive },
      cancelText: 'Cancel',
      content: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <strong>{scopeSummary}</strong>
          <span>This is not limited to one facility. {monthWideScaleCopy}</span>
          {hasSegment ? null : (
            <span>
              No payment stage is selected, so this covers <strong>all statuses</strong> — Pending
              CO, Pending HM and Pending CFO claims that are still being processed, as well as
              Paid ones.
            </span>
          )}
          <span>
            {isArchive
              ? 'Archived claims drop out of the default list for every facility at once. They can be restored later from the Archived filter.'
              : 'Restored claims return to the default list for every facility at once.'}
          </span>
        </div>
      ),
      onOk: () => submitRequest(),
    });
  };

  return (
    <Modal
      title={title || (isArchive ? 'Archive claims' : 'Unarchive claims')}
      open={open}
      onCancel={onClose}
      onOk={handleConfirm}
      okText={(monthWideSelected || multiMonth)
        ? (isArchive ? 'Archive all facilities…' : 'Restore all facilities…')
        : (isArchive ? 'Archive' : 'Unarchive')}
      okButtonProps={{
        danger: isArchive,
        loading,
        disabled: !canSubmit || (isArchive && eligible === 0),
      }}
      cancelButtonProps={{ disabled: loading }}
      destroyOnClose
    >
      {showScopePicker ? (
        <div style={{ marginBottom: 16 }}>
          <span style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#64748b' }}>
            Scope
          </span>
          <Radio.Group
            value={monthWide ? 'month' : 'facility'}
            onChange={(e) => {
              setMonthWide(e.target.value === 'month');
              setPreview(null);
            }}
            optionType="button"
            buttonStyle="solid"
            disabled={loading}
          >
            <Radio.Button value="facility">This facility</Radio.Button>
            <Radio.Button value="month">All facilities this month</Radio.Button>
          </Radio.Group>
        </div>
      ) : null}

      {showFacilityNotices && facilitySource === 'directory' ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Showing every facility, not just this month's"
          description={(
            <>
              <div>
                The list of facilities with {archivedWord}{facilityClaimsPhrase} could not be
                loaded ({facilityFallbackReason}), so this is the full facility directory instead.
                It includes facilities with no claims that month — archiving one of those does
                nothing — and it stops at {DIRECTORY_FALLBACK_LIMIT} facilities, so a facility
                that does have claims may be missing.
              </div>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">
                  Re-pick the month, or close and re-open this dialog, to try the month list again.
                </Text>
              </div>
            </>
          )}
        />
      ) : null}
      {showFacilityNotices && facilitiesTruncated ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Facility list cut short"
          description={(
            <>
              {facilitiesTruncated.toLocaleString()} facilities have {archivedWord}
              {facilityClaimsPhrase} — more than this picker loads. Only the first{' '}
              {(FACILITY_PAGE_SIZE * FACILITY_MAX_PAGES).toLocaleString()} are listed, so a
              facility may be missing. Narrow the month, or archive from the facility's own
              claims list instead.
            </>
          )}
        />
      ) : null}
      {showFacilityNotices && droppedFacilityName ? (
        <Alert
          type="info"
          showIcon
          closable
          onClose={() => setDroppedFacilityName(null)}
          style={{ marginBottom: 12 }}
          message="Facility cleared"
          description={(
            <>
              <strong>{droppedFacilityName}</strong> has no {archivedWord}{facilityClaimsPhrase},
              so it was cleared. Pick one of the facilities that does.
            </>
          )}
        />
      ) : null}

      {pickerMode ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
          <label style={{ display: monthWideSelected ? 'none' : 'block' }}>
            <span style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#64748b' }}>
              Facility
            </span>
            {lockedFacility ? (
              <Input value={displayFacility || 'Your facility'} disabled />
            ) : (
              <>
                <Select
                  showSearch
                  allowClear
                  style={{ width: '100%' }}
                  disabled={!month}
                  placeholder={month ? facilityPlaceholder : 'Choose a month first'}
                  loading={facilitiesLoading}
                  value={facilityId}
                  optionFilterProp="label"
                  options={facilityOptions}
                  notFoundContent={facilitiesLoading ? <Spin size="small" /> : facilityEmptyText}
                  onChange={(value) => {
                    const next = value != null ? Number(value) : null;
                    setFacilityId(next);
                    const match = facilityOptions.find((o) => o.value === next);
                    setFacilityName(match?.name || '');
                    setDroppedFacilityName(null);
                    setPreview(null);
                  }}
                />
                {facilityHint ? (
                  <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
                    {facilityHint}
                  </Text>
                ) : null}
              </>
            )}
          </label>
          <label style={{ display: 'block' }}>
            <span style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#64748b' }}>
              Month
            </span>
            <DatePicker
              picker="month"
              style={{ width: '100%' }}
              value={month ? dayjs(`${month}-01`) : null}
              onChange={(value) => {
                setMonth(value ? value.format('YYYY-MM') : null);
                setPreview(null);
              }}
            />
          </label>
        </div>
      ) : null}

      {loading && !preview ? (
        <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
      ) : (
        <>
          {preview?.error ? (
            <Alert type="error" showIcon message={preview.error} style={{ marginBottom: 12 }} />
          ) : null}
          {monthWideSelected && effectiveMonth && !preview?.error ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={isArchive ? 'This archives every facility' : 'This restores every facility'}
              description={(
                <>
                  <div>{monthWideScaleCopy}</div>
                  {monthWideRestore ? (
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary">
                        The server does not return a count for a month-wide restore, so the number
                        of claims cannot be previewed here.
                      </Text>
                    </div>
                  ) : null}
                </>
              )}
            />
          ) : null}
          {showAllStatusesNote ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="This covers all statuses"
              description={
                isArchive
                  ? 'No payment stage is selected here, so every claim in this scope is included — Pending CO, Pending HM and Pending CFO claims that are still being processed, as well as Paid ones.'
                  : 'No payment stage is selected here, so every archived claim in this scope is restored, whatever stage it was at.'
              }
            />
          ) : null}
          {resolvedRequest ? (
            <div
              style={{
                marginBottom: 12,
                padding: '8px 12px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 6,
              }}
            >
              <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b' }}>
                {isArchive ? 'Will archive' : 'Will restore'}
              </span>
              <span style={{ fontWeight: 600 }}>{scopeSummary}</span>
            </div>
          ) : null}
          <p style={{ marginTop: 0 }}>{confirmCopy}</p>
          {isArchive && eligible != null && !preview?.error ? (
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Archived claims drop out of the default list. Use the Archived filter to view them later.
            </Text>
          ) : null}
          {skipped.length > 0 ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={`${skipped.length} item(s) will be skipped`}
              description={(
                <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                  {skipped.slice(0, 5).map((item, idx) => (
                    <li key={item?.claims_id || item?.id || idx}>
                      <Text type="secondary">{item?.reason || item?.message || 'Not archivable'}</Text>
                    </li>
                  ))}
                  {skipped.length > 5 ? <li>…and {skipped.length - 5} more</li> : null}
                </ul>
              )}
            />
          ) : null}
          <label style={{ display: 'block', marginBottom: 6, fontSize: 12, fontWeight: 600, color: '#64748b' }}>
            Reason (optional)
          </label>
          <Input.TextArea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={isArchive ? 'e.g. June close-out' : 'e.g. Reopened for review'}
          />
        </>
      )}
    </Modal>
  );
}
